import { zodResolver } from "@hookform/resolvers/zod";
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { CheckboxElement, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { BaseAutocomplete } from "@/components/a-autocomplete";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";
import {
  ClubManager,
  ClubWithEnabled,
} from "@/module/room-configs-module/manager-config-module/utils";

export interface ClubManagerFormData
  extends Partial<Omit<ClubManager, "created_at" & "updated_at">> {
  name: string;
  auth_groups: Set<string>;
  enabled: boolean;
  data: {
    username: string;
    clubs: ClubWithEnabled[];
  };
}

const thresholdConfigSchemaEv = () =>
  z.object({
    enabled: z.boolean(),
    threshold: z.number(),
  });

const goldBuyConfigSchema = thresholdConfigSchemaEv().extend({
  packs: z.number(),
});

const clubSchema = z.object({
  enabled: z.boolean().default(false),
  cid: z.string().min(1),
  aid: z.string().min(1),
  group: z.string().min(1),
  level: thresholdConfigSchemaEv(),
  kickout: z.object({
    low_gold: thresholdConfigSchemaEv(),
    inactive: thresholdConfigSchemaEv(),
    unauthorized: thresholdConfigSchemaEv(),
  }),
  alc: z.object({
    distribute: thresholdConfigSchemaEv(),
  }),
  gold: z.object({
    buy: goldBuyConfigSchema,
    distribute: thresholdConfigSchemaEv(),
  }),
  gems: thresholdConfigSchemaEv(),
});

const schema = z.object({
  name: z.string().min(1, "Required"),
  auth_groups: z.set(z.string()).nonempty("Select at least one group"),
  enabled: z.boolean(),
  data: z.object({
    username: z.string().min(1, "Required"),
    clubs: z.array(clubSchema).nonempty("At least one club is required"),
  }),
});

const defaultInitialClub: ClubWithEnabled = {
  //empty club object
  enabled: false,
  cid: "",
  aid: "",
  group: "",
  level: { enabled: false, threshold: 0 },
  kickout: {
    low_gold: { enabled: false, threshold: 0 },
    inactive: { enabled: false, threshold: 0 },
    unauthorized: { enabled: false, threshold: 0 },
  },
  alc: {
    distribute: { enabled: false, threshold: 0 },
  },
  gold: {
    buy: { enabled: false, threshold: 0, packs: 0 },
    distribute: { enabled: false, threshold: 0 },
  },
  gems: { enabled: false, threshold: 0 },
};

const defaultInitialData = {
  name: "",
  auth_groups: [],
  enabled: true,
  data: {
    username: "",
    clubs: [],
  },
};

export const ClubManagerModal = ({
  open,
  mode = "add",
  initialData,
  onSave,
  onCancel,
  confirmLoading = false,
  disabled = false,
  loadingAuthGroups = false,
}: {
  open: boolean;
  mode?: "add" | "edit";
  initialData?: ClubManagerFormData;
  onSave?: (data: ClubManagerFormData) => void;
  onCancel: () => void;
  confirmLoading?: boolean;
  disabled?: boolean;
  loadingAuthGroups?: boolean;
}) => {
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ClubManagerFormData>({
    resolver: zodResolver(schema),
    defaultValues: initialData ?? defaultInitialData,
  });
  const { t } = useTranslation();

  const clubs = useWatch({ control, name: "data.clubs" });

  const { data: authGroupsRs } = useAuthGroupsQuery();
  const authGroupsOptions = useMemo(() => {
    return authGroupsRs?.data.map((it) => it.name).sort((a, b) => a.localeCompare(b)) ?? [];
  }, [authGroupsRs?.data]);

  const [editingClub, setEditingClub] = useState<ClubWithEnabled | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [clubModalOpen, setClubModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      reset(initialData || defaultInitialData);
    }
  }, [initialData, open, reset]);

  const handleClubSave = (club: ClubWithEnabled) => {
    const clubs = [...getValues("data.clubs")];
    if (editIndex != null) {
      clubs[editIndex] = club;
    } else {
      clubs.push(club);
    }
    setValue("data.clubs", clubs);
    setClubModalOpen(false);
    setEditingClub(null);
    setEditIndex(null);
  };

  const handleClubDelete = (index: number, cid: string) => {
    setValue("data.clubs", [
      ...getValues("data.clubs").filter((_, i) => {
        return i !== index;
      }),
    ]);
  };

  const onSubmit = (data: ClubManagerFormData) => {
    onSave?.(data);
    reset();
  };

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      onClose={(event, reason) => {
        if (
          reason === "backdropClick" &&
          isDirty &&
          !window.confirm("Data has been changed. Do you want discard changes?")
        )
          return; //ask question if changes==true and click outSide
        onCancel?.();
      }}
    >
      <DialogTitle>
        {mode === "add" ? t("addNewClubManager") : t("editClubManager")}
        <IconButton onClick={onCancel} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}
        >
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                fullWidth
                disabled={mode === "edit"}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />

          <Controller
            name="auth_groups"
            control={control}
            render={({ field: { onChange, value } }) => (
              <BaseAutocomplete
                multiple
                options={authGroupsOptions}
                loading={loadingAuthGroups}
                value={[...value]}
                onChange={(_, newValue) => onChange(new Set(newValue))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Auth Groups"
                    fullWidth
                    error={!!errors.auth_groups}
                    helperText={errors.auth_groups?.message}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAuthGroups ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />

          <CheckboxElement
            control={control}
            name="enabled"
            label={"Enabled" /*t("enabled")*/}
            slotProps={{
              input: {
                disabled: mode === "edit",
              },
            }}
          />

          <Controller
            name="data.username"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Username"
                fullWidth
                disabled={disabled}
                error={!!errors.data?.username}
                helperText={errors.data?.username?.message}
              />
            )}
          />

          {/* --- CLUBS TABLE --- */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3>Clubs</h3>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditingClub(defaultInitialClub);
                  setClubModalOpen(true);
                }}
              >
                Add Club
              </Button>
            </div>
            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 10 }}>Enabled</TableCell>
                  <TableCell sx={{ width: 10 }}>CID</TableCell>
                  <TableCell sx={{ width: 10 }}>AID</TableCell>
                  <TableCell sx={{ width: 10 }}>Group</TableCell>

                  <TableCell sx={{ width: 10 }}>Level</TableCell>
                  <TableCell sx={{ width: 10 }}>Kickout Low</TableCell>
                  <TableCell sx={{ width: 10 }}>Kickout Inactive</TableCell>
                  <TableCell sx={{ width: 10 }}>Kickout Unauth-d</TableCell>
                  <TableCell sx={{ width: 10 }}>Alc Distribute</TableCell>
                  <TableCell sx={{ width: 10 }}>Gold Buy</TableCell>
                  <TableCell sx={{ width: 10 }}>Gold Distribute</TableCell>
                  <TableCell sx={{ width: 10 }}>Gems</TableCell>
                  <TableCell sx={{ width: 14 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clubs.map((club, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {
                        <Controller
                          name={`data.clubs.${index}.enabled`}
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          )}
                        />
                      }
                    </TableCell>
                    <TableCell>{club.cid}</TableCell>
                    <TableCell>{club.aid}</TableCell>
                    <TableCell>{club.group}</TableCell>

                    <TableCell>
                      {String(club.kickout?.low_gold?.enabled ? "enabled" : "disabled")}
                      <br />
                      threshold: {club.level.threshold ?? "-"}
                    </TableCell>

                    <TableCell>
                      {String(club.kickout?.low_gold?.enabled ? "enabled" : "disabled")}
                      <br />
                      threshold: {club.kickout?.low_gold?.threshold ?? "-"}
                    </TableCell>

                    <TableCell>
                      {String(club.kickout?.low_gold?.enabled ? "enabled" : "disabled")}
                      <br />
                      threshold: {club.kickout?.inactive?.threshold ?? "-"}
                    </TableCell>

                    <TableCell>
                      {String(club.kickout?.low_gold?.enabled ? "enabled" : "disabled")}
                      <br />
                      threshold: {club.kickout?.unauthorized?.threshold ?? "-"}
                    </TableCell>

                    <TableCell>
                      {String(club.kickout?.low_gold?.enabled ? "enabled" : "disabled")}
                      <br />
                      threshold: {club.alc?.distribute?.threshold ?? "-"}
                    </TableCell>

                    <TableCell>
                      {String(club.kickout?.low_gold?.enabled ? "enabled" : "disabled")}
                      <br />
                      threshold: {club.gold?.buy?.threshold ?? "-"}
                      <br />
                      packs: {club.gold?.buy?.packs ?? "-"}
                    </TableCell>

                    <TableCell>
                      {String(club.kickout?.low_gold?.enabled ? "enabled" : "disabled")}
                      <br />
                      threshold: {club.gold?.distribute?.threshold ?? "-"}
                    </TableCell>

                    <TableCell>
                      {String(club.kickout?.low_gold?.enabled ? "enabled" : "disabled")}
                      <br />
                      threshold: {club.gems?.threshold ?? "-"}
                    </TableCell>
                    <TableCell align="right">
                      {/* actions*/}
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingClub(club);
                          setEditIndex(index);
                          setClubModalOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingClub(club);
                          setClubModalOpen(true);
                        }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleClubDelete(index, club.cid)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!!errors.data?.clubs && (
              <FormHelperText error>{errors.data?.clubs?.message as string}</FormHelperText>
            )}
          </div>

          <ModalActionsButtons
            onCancelClick={onCancel}
            onConfirmClick={() => void 0}
            loading={confirmLoading}
          />
        </form>
      </DialogContent>

      <ClubModal
        open={clubModalOpen}
        initialData={editingClub}
        onSave={handleClubSave}
        onCancel={() => {
          setClubModalOpen(false);
          setEditingClub(null);
          setEditIndex(null);
        }}
      />
    </Dialog>
  );
};

const ClubModal = ({
  open,
  initialData,
  onSave,
  onCancel,
}: {
  open: boolean;
  initialData: ClubWithEnabled | null;
  onSave: (club: ClubWithEnabled) => void;
  onCancel: () => void;
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ClubWithEnabled>({
    resolver: zodResolver(clubSchema),
    defaultValues: initialData ?? defaultInitialClub,
  });

  useEffect(() => {
    if (open && initialData) reset(initialData);
  }, [initialData, open, reset]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="lg" fullWidth>
      <DialogTitle>
        {initialData ? "Edit Club" : "Add Club"}
        <IconButton onClick={onCancel} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form
          onSubmit={handleSubmit((data) => onSave(data))}
          style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}
        >
          <CheckboxElement name="enabled" control={control} label="Club enabled" />

          <TextFieldElement
            name="cid"
            control={control}
            label="CID"
            placeholder="CID"
            fullWidth
            error={!!errors.cid}
            helperText={errors.cid?.message}
          />

          <TextFieldElement
            name="aid"
            control={control}
            label="AID"
            placeholder="AID"
            fullWidth
            error={!!errors.aid}
            helperText={errors.aid?.message}
          />

          <TextFieldElement
            name="group"
            control={control}
            label="Group"
            placeholder="Group"
            fullWidth
            error={!!errors.group}
            helperText={errors.group?.message}
          />

          <Grid container spacing={2}>
            {/* Секция Level */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Level
                </Typography>

                <CheckboxElement
                  name="level.enabled"
                  control={control}
                  label="Level Enabled"
                />
                <TextFieldElement
                  name="level.threshold"
                  control={control}
                  label="Level Threshold"
                  type="number"
                  size="small"
                  fullWidth
                  slotProps={{ input: { inputProps: { min: 0 } } }}
                />
              </Paper>
            </Grid>

            {/* Секция Alc */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Alc
                </Typography>
                <CheckboxElement
                  name="alc.distribute.enabled"
                  control={control}
                  label="Alc Distribute Enabled"
                />
                <TextFieldElement
                  name="alc.distribute.threshold"
                  control={control}
                  label="Alc Distribute Threshold"
                  type="number"
                  size="small"
                  fullWidth
                  slotProps={{ input: { inputProps: { min: 0 } } }}
                />
              </Paper>
            </Grid>
            {/* Секция Gems */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Gems
                </Typography>

                <CheckboxElement name="gems.enabled" control={control} label="Gems Enabled" />

                <TextFieldElement
                  name="gems.threshold"
                  control={control}
                  label="Gems Threshold"
                  type="number"
                  size="small"
                  fullWidth
                  slotProps={{ input: { inputProps: { min: 0 } } }}
                />
              </Paper>
            </Grid>
          </Grid>

          {/* Секция Kickout */}
          <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Kickout
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <CheckboxElement
                  name="kickout.low_gold.enabled"
                  control={control}
                  label="Kickout Low Gold Enabled"
                />
                <TextFieldElement
                  name="kickout.low_gold.threshold"
                  control={control}
                  label="Low Gold Threshold"
                  type="number"
                  size="small"
                  fullWidth
                  slotProps={{ input: { inputProps: { min: 0 } } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <CheckboxElement
                  name="kickout.inactive.enabled"
                  control={control}
                  label="Kickout Inactive Enabled"
                />
                <TextFieldElement
                  name="kickout.inactive.threshold"
                  control={control}
                  label="Inactive Threshold"
                  type="number"
                  size="small"
                  fullWidth
                  slotProps={{ input: { inputProps: { min: 0 } } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <CheckboxElement
                  name="kickout.unauthorized.enabled"
                  control={control}
                  label="Kickout Unauthorized Enabled"
                />
                <TextFieldElement
                  name="kickout.unauthorized.threshold"
                  control={control}
                  label="Unauthorized Threshold"
                  type="number"
                  size="small"
                  fullWidth
                  slotProps={{ input: { inputProps: { min: 0 } } }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Секция Gold */}
          <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Gold
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <CheckboxElement
                  name="gold.buy.enabled"
                  control={control}
                  label="Gold Buy Enabled"
                />
                <TextFieldElement
                  name="gold.buy.threshold"
                  control={control}
                  label="Gold Buy Threshold"
                  type="number"
                  size="small"
                  fullWidth
                  slotProps={{ input: { inputProps: { min: 0 } } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextFieldElement
                  style={{ marginTop: "42px" }}
                  name="gold.buy.packs"
                  control={control}
                  label="Gold Buy Packs"
                  type="number"
                  size="small"
                  fullWidth
                  slotProps={{ input: { inputProps: { min: 0 } } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <CheckboxElement
                  name="gold.distribute.enabled"
                  control={control}
                  label="Gold Distribute Enabled"
                />
                <TextFieldElement
                  name="gold.distribute.threshold"
                  control={control}
                  label="Gold Distribute Threshold"
                  type="number"
                  size="small"
                  fullWidth
                  slotProps={{ input: { inputProps: { min: 0 } } }}
                />
              </Grid>
            </Grid>
          </Paper>

          <ModalActionsButtons onCancelClick={onCancel} onConfirmClick={() => void 0} />
        </form>
      </DialogContent>
    </Dialog>
  );
};
